import { Page, Text, View, Document, StyleSheet, Image } from '@react-pdf/renderer';
import logo from "@/assets/ppm-logo.png";
import { format } from 'date-fns';
import { dateformat } from '@/utils/GlobalConstants';
import { CharterReport } from '@/Pages/Reports';
import TiptapToPDF from '../ui/tiptap/tiptap_toPDF';

// Helper function to convert HSL CSS variable to hex color for react-pdf
function hslToHex(h: number, s: number, l: number): string {
    l /= 100;
    const a = s * Math.min(l, 1 - l) / 100;
    const f = (n: number) => {
        const k = (n + h / 30) % 12;
        const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
        return Math.round(255 * color).toString(16).padStart(2, '0');
    };
    return `#${f(0)}${f(8)}${f(4)}`;
}

// Get theme colors from CSS variables
function getThemeColors() {
    const root = getComputedStyle(document.documentElement);
    
    const parseHSL = (varName: string): string => {
        const value = root.getPropertyValue(varName).trim();
        const parts = value.split(/[\s,]+/).map(v => parseFloat(v));
        if (parts.length >= 3) {
            return hslToHex(parts[0], parts[1], parts[2]);
        }
        return '#000000';
    };

    return {
        header: parseHSL('--table-header'),
        headerForeground: parseHSL('--table-header-foreground'),
        subheader: parseHSL('--table-subheader'),
        subheaderForeground: parseHSL('--table-subheader-foreground'),
        background: parseHSL('--background'),
        foreground: parseHSL('--foreground'),
        border: parseHSL('--border'),
        cardBackground: parseHSL('--card'),
        tableBody: parseHSL('--table-body'),
        chartRed: parseHSL('--chart-red'),
        chartAmber: parseHSL('--chart-amber'),
        chartGreen: parseHSL('--chart-green'),
        tableAlternate: parseHSL('--table-alternate-row'),
    };
}

// Create styles function that takes theme colors
const createStyles = (colors: ReturnType<typeof getThemeColors>) => StyleSheet.create({
    header: {
        flexDirection: 'row',
        backgroundColor: colors.header,
        color: colors.headerForeground,
        padding: 5,
        fontWeight: 'heavy',
    },
    fieldHeader: {
        backgroundColor: colors.header,
        color: colors.headerForeground,
        fontWeight: 'heavy',
        border: 'none',
    },
    text: {
        fontSize: 8,
        padding: 5,
        color: colors.foreground,
        //textAlign: 'justify',
        //fontFamily: 'Times-Roman'
    },
    page: {
        flexDirection: 'column',
        backgroundColor: colors.background
    },
    body: {
        flexDirection: 'row',
    },
    section: {
        flexDirection: 'column',
        width: '33%',
        paddingHorizontal: 10
    }
});

function getStatusColor(status: string, colors: ReturnType<typeof getThemeColors>) {
    if (status === 'Low' || status === 'Approved') {
        return colors.chartGreen;
    } else if (status === 'Medium') {
        return colors.chartAmber;
    } else if (status === 'High' || status === 'Rejected') {
        return colors.chartRed;
    } else {
        return colors.chartGreen;
    }
}

interface CharterReportProps {
    reportData: CharterReport
    projectTypeId: number
}

function CharterReportPDF(charterReport: CharterReportProps) {
    const colors = getThemeColors();
    const styles = createStyles(colors);
    
    return (
        <Document title={charterReport.reportData.projectCharter.projectName + " Charter"} author="Explic8">
            <Page size="A4" orientation='landscape' style={styles.page}>
                <View style={styles.header}>
                    <Text>{charterReport.reportData.projectCharter.projectName + " - Charter"}</Text>
                </View>
                <Image src={logo} style={{ width: "120px", marginVertical: "5px", marginHorizontal: "10px" }} />
                <View style={styles.body}>
                    <View style={styles.section}>{/* left of the page */}
                        <Text style={styles.text}>Objective</Text>
                        <View style={{ border: `1px solid ${colors.border}`, padding: 5, marginBottom: 5, borderRadius: 5 }}>
                            {/* <Text style={styles.text}>{charterReport.reportData.projectCharter.objective}</Text> */}
                            <TiptapToPDF json={charterReport.reportData.projectCharter.objective} />
                        </View>
                        <Text style={styles.text}>In Scope</Text>
                        <View style={{ border: `1px solid ${colors.border}`, padding: 5, marginBottom: 5, borderRadius: 5 }}>
                            <TiptapToPDF json={charterReport.reportData.projectCharter.inScope} />
                        </View>
                        <Text style={styles.text}>Out of Scope</Text>
                        <View style={{ border: `1px solid ${colors.border}`, padding: 5, marginBottom: 5, borderRadius: 5 }}>
                            <TiptapToPDF json={charterReport.reportData.projectCharter.outOfScope} />
                        </View>
                    </View>
                    <View style={styles.section}> {/* middle of the page */}
                        <View style={{ flexDirection: 'column', }}>
                            <View style={{ flexDirection: 'row', backgroundColor: colors.header, color: colors.headerForeground, padding: 5, borderTopLeftRadius: 5, borderTopRightRadius: 5 }}>
                                <View style={{ flexDirection: 'column', alignItems: 'center', width: '75%' }}>
                                    <Text style={{ fontSize: 10, fontWeight: 'heavy' }}>Major Events</Text>
                                </View>
                                <View style={{ flexDirection: 'column', alignItems: 'center' }}>
                                    <Text style={{ fontSize: 10, fontWeight: 'extrabold' }}>Finish Date</Text>
                                </View>
                            </View>
                            {charterReport.reportData.projectCharter.majorMilestones?.map((milestone) => {
                                if (milestone.tasks && milestone.tasks.length > 0) {
                                    return (
                                        <View key={milestone.workPackageId}>
                                            <View style={{ flexDirection: 'row', backgroundColor: colors.subheader, justifyContent: 'space-between', color: colors.subheaderForeground }}>
                                                <View style={{ flexDirection: 'column', alignItems: 'center' }}>
                                                    <Text style={styles.text}>{milestone.workPackageName}</Text>
                                                </View>
                                                <View style={{ flexDirection: 'column', alignItems: 'center' }}>
                                                    {/* empty column */}
                                                </View>
                                            </View>
                                            {milestone.tasks.map((task) => {
                                                return (
                                                    <View key={task.id} style={{ flexDirection: 'row', justifyContent: 'space-between', backgroundColor: colors.tableBody, color: colors.headerForeground }}>
                                                        <View style={{ flexDirection: 'column', alignItems: 'center' }}>
                                                            <Text style={styles.text}>{task.name}</Text>
                                                        </View>
                                                        <View style={{ flexDirection: 'column', alignItems: 'center' }}>
                                                            <Text style={styles.text}>{format(new Date(task.finishDate), dateformat)}</Text>
                                                        </View>
                                                    </View>
                                                );
                                            })}
                                        </View>
                                    );
                                }
                                return null;
                            })}
                        </View>
                    </View>
                    <View style={styles.section}>{/* Right side of the page */}
                        <Text style={styles.text}>Project Sponsors</Text>
                        <View style={{ border: `1px solid ${colors.border}`, padding: 5, marginBottom: 5, borderRadius: 5 }}>
                            {charterReport.reportData.projectCharter.sponsors.map((sponsor) => {
                                return (
                                    <View key={sponsor.id} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <Text style={styles.text}>{sponsor.forename + " " + sponsor.surname}</Text>
                                        <Text style={styles.text}>{sponsor.userName}</Text>
                                    </View>
                                )
                            })}
                        </View>
                        <Text style={styles.text}>Project Management</Text>
                        <View style={{ border: `1px solid ${colors.border}`, padding: 5, marginBottom: 5, borderRadius: 5 }}>
                            {charterReport.reportData.projectCharter.projectManagers.map((manager) => {
                                return (
                                    <View key={manager.id} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <Text style={styles.text}>{manager.forename + " " + manager.surname}</Text>
                                        <Text style={styles.text}>{manager.userName}</Text>
                                    </View>
                                )
                            })}
                        </View>

                        <Text style={styles.text}>Project Team</Text>
                        <View style={{ border: `1px solid ${colors.border}`, padding: 5, marginBottom: 5, borderRadius: 5 }}>
                            {charterReport.reportData.projectCharter.teamMembers.map((teamMember) => {
                                return (
                                    <View key={teamMember.id} style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                                        <Text style={styles.text}>{teamMember.forename + " " + teamMember.surname}</Text>
                                        <Text style={styles.text}>{teamMember.userName}</Text>
                                    </View>
                                )
                            })}
                        </View>
                        <Text style={styles.text}>Critical Success Factors</Text>
                        <View style={{ border: `1px solid ${colors.border}`, padding: 5, marginBottom: 5, borderRadius: 5 }}>
                            <TiptapToPDF json={charterReport.reportData.projectCharter.criticalSuccessFactors} />
                        </View>
                    </View>
                </View>
            </Page>
            {charterReport.reportData.deliverables.length > 0 && (
                <Page size="A4" orientation='landscape' style={styles.page}>
                    <View style={styles.header}>
                        <Text>{charterReport.reportData.projectCharter.projectName + " - Deliverables"}</Text>
                    </View>
                    <Image src={logo} style={{ width: "120px", marginVertical: "5px", marginHorizontal: "10px" }} />
                    <View style={{ flexDirection: 'column', width: '100%', padding: 5 }}>
                        <View style={{ flexDirection: 'column', paddingBottom: 10, borderRadius: 5 }}>
                            <View style={{ flexDirection: 'row', justifyContent: 'center', backgroundColor: colors.header, color: colors.headerForeground, borderTopLeftRadius: 5, borderTopRightRadius: 5, }}>
                                <View style={{ flexDirection: 'column', width: '20%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Work Package</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '40%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Deliverable</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '20%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Planned Finish</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '20%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Deliverables Owner</Text>
                                </View>
                            </View>
                            {charterReport.reportData.deliverables.map((deliverable, index) => {
                                return (
                                    <View style={{ flexDirection: 'row', justifyContent: 'center', backgroundColor: index % 2 === 0 ? colors.cardBackground : colors.tableAlternate }}>
                                        <View style={{ flexDirection: 'column', width: '20%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto' }]}>{deliverable.workPackage}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '40%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto' }]}>{deliverable.deliverable}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '20%', textAlign: 'center' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto' }]}>{format(new Date(deliverable.plannedFinish), dateformat)}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '20%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto', textAlign: 'center' }]}>{deliverable.owner}</Text>
                                        </View>
                                    </View>
                                )
                            })}
                        </View>
                    </View>
                </Page>
            )}
            {charterReport.reportData.changeRequests.length > 0 && (
                <Page size="A4" orientation='landscape' style={styles.page}>
                    <View style={styles.header}>
                        <Text>{charterReport.reportData.projectCharter.projectName + " - Project Change Requests"}</Text>
                    </View>
                    <Image src={logo} style={{ width: "120px", marginVertical: "5px", marginHorizontal: "10px" }} />
                    <View style={{ flexDirection: 'column', width: '100%', padding: 5 }}>
                        <View style={{ flexDirection: 'column', paddingBottom: 10, borderRadius: 5 }}>
                            <View style={{ flexDirection: 'row', justifyContent: 'center', backgroundColor: colors.header, color: colors.headerForeground, borderTopLeftRadius: 5, borderTopRightRadius: 5, }}>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Workstream</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Work Package</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '20%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Description</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Requested From</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Date</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Closed Date</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Impact Rating</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Review Status</Text>
                                </View>
                                <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', }}>
                                    <Text style={styles.text}>Notes</Text>
                                </View>
                            </View>
                            {charterReport.reportData.changeRequests.map((changeRequest, index) => {
                                return (
                                    <View style={{ flexDirection: 'row', justifyContent: 'center', backgroundColor: index % 2 === 0 ? colors.cardBackground : colors.tableAlternate }}>
                                        <View style={{ flexDirection: 'column', width: '10%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto' }]}>{changeRequest.workstream}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '10%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto' }]}>{changeRequest.workPackage}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '20%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto' }]}>{changeRequest.description}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto' }]}>{changeRequest.owner}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '10%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto', textAlign: 'center' }]}>{format(new Date(changeRequest.date), dateformat)}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '10%' }}>
                                            <Text style={[styles.text, { marginVertical: 'auto', textAlign: 'center' }]}>{format(new Date(changeRequest.closedDate!), dateformat)}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', padding: 5 }}>
                                            <Text style={[styles.text, { color: colors.headerForeground, marginVertical: 'auto', backgroundColor: getStatusColor(changeRequest.rating, colors) }]}>{changeRequest.rating}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '10%', textAlign: 'center', padding: 5 }}>
                                            <Text style={[styles.text, { color: colors.headerForeground, marginVertical: 'auto', backgroundColor: getStatusColor(changeRequest.reviewStatus, colors) }]}>{changeRequest.reviewStatus}</Text>
                                        </View>
                                        <View style={{ flexDirection: 'column', width: '10%' }}>
                                            <Text style={styles.text}>{changeRequest.notes}</Text>
                                        </View>
                                    </View>
                                )
                            })}
                        </View>
                    </View>
                </Page>
            )}
        </Document>
    )
}

export default CharterReportPDF